import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getUserLocale } from '../utils/i18n';

// ── Constants ─────────────────────────────────────────────────
const WC_PHASES = [
  { key: 'groups', label: 'Grupos', icon: 'grid_view' },
  { key: 'r16', label: 'Octavos', icon: 'call_split' },
  { key: 'qf', label: 'Cuartos', icon: 'social_leaderboard' },
  { key: 'sf', label: 'Semis', icon: 'star_half' },
  { key: 'final', label: 'Final', icon: 'emoji_events' },
];
const CURRENT_PHASE_INDEX = 1;

const STATUS_BADGE = {
  ACTIVE: { label: 'En curso', cls: 'bg-emerald-500/12 text-emerald-700', dot: 'bg-emerald-500' },
  UPCOMING: { label: 'Próximo', cls: 'bg-blue-500/12 text-blue-700', dot: 'bg-blue-500' },
  FINISHED: { label: 'Finalizado', cls: 'bg-surface-container-high text-on-surface-variant', dot: 'bg-on-surface-variant/40' },
};

// ── Helpers ───────────────────────────────────────────────────
const getTeamColor = (name) => {
  const palette = ['#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#3b82f6','#8b5cf6','#ec4899','#06b6d4','#d946ef'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const scorePrediction = (pred, match, scoring = { exact: 3, winner: 1 }) => {
  if (!pred || match.homeScore == null || match.awayScore == null) return null;
  const ph = Number(pred.home), pa = Number(pred.away);
  if (isNaN(ph) || isNaN(pa)) return null;
  if (ph === match.homeScore && pa === match.awayScore) return { points: scoring.exact, type: 'exact' };
  const pW = Math.sign(ph - pa), aW = Math.sign(match.homeScore - match.awayScore);
  if (pW === aW) return { points: scoring.winner, type: 'winner' };
  return { points: 0, type: 'wrong' };
};

const fmtMatchDate = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = d - now;
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const locale = getUserLocale();
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const date = d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  if (diff < -3600000) return { main: date, sub: time, tag: null };
  if (diff < 0) return { main: 'En juego', sub: time, tag: 'live' };
  if (hours < 3) return { main: 'Pronto', sub: `Hoy ${time}`, tag: 'urgent' };
  if (hours < 24) return { main: `En ${hours}h`, sub: `Hoy ${time}`, tag: 'soon' };
  if (days <= 1) return { main: 'Mañana', sub: time, tag: null };
  if (days < 7) return { main: `En ${days} días`, sub: `${date} · ${time}`, tag: null };
  return { main: date, sub: time, tag: null };
};

const groupByRound = (list) => {
  const groups = {};
  list.forEach(m => {
    const key = m.round || 'Partidos';
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  });
  return Object.entries(groups);
};

// ── Mock Data ─────────────────────────────────────────────────
const MOCK_PRODES = [
  {
    id: 'p1', communityId: 'c1', communityName: 'Fútbol Argentino',
    status: 'ACTIVE', createdBy: 'SELF',
    scoring: { exact: 3, winner: 1 },
    participantCount: 24, totalMatches: 34, playedMatches: 18, joined: true,
  },
  {
    id: 'p2', communityId: 'c2', communityName: 'Amigos del Cole',
    status: 'ACTIVE', createdBy: 'other',
    scoring: { exact: 3, winner: 1 },
    participantCount: 8, totalMatches: 8, playedMatches: 4, joined: true,
  },
  {
    id: 'p3', communityId: 'c3', communityName: 'Oficina Tech',
    status: 'UPCOMING', createdBy: 'other',
    scoring: { exact: 3, winner: 1 },
    participantCount: 15, totalMatches: 0, playedMatches: 0, joined: false,
  },
];

const MOCK_MATCHES = {
  p1: [
    // Grupo A - Fecha 1
    { id: 'a1', round: 'Grupo A - Fecha 1', homeTeam: 'USA', homeFlag: '\u{1F1FA}\u{1F1F8}', awayTeam: 'Marruecos', awayFlag: '\u{1F1F2}\u{1F1E6}', matchDate: '2026-06-11T13:00:00', status: 'FINISHED', homeScore: 2, awayScore: 1 },
    { id: 'a2', round: 'Grupo A - Fecha 1', homeTeam: 'Canad\u00e1', homeFlag: '\u{1F1E8}\u{1F1E6}', awayTeam: 'Ecuador', awayFlag: '\u{1F1EA}\u{1F1E8}', matchDate: '2026-06-11T16:00:00', status: 'FINISHED', homeScore: 0, awayScore: 1 },
    // Grupo A - Fecha 2
    { id: 'a3', round: 'Grupo A - Fecha 2', homeTeam: 'USA', homeFlag: '\u{1F1FA}\u{1F1F8}', awayTeam: 'Ecuador', awayFlag: '\u{1F1EA}\u{1F1E8}', matchDate: '2026-06-15T13:00:00', status: 'FINISHED', homeScore: 1, awayScore: 1 },
    { id: 'a4', round: 'Grupo A - Fecha 2', homeTeam: 'Marruecos', homeFlag: '\u{1F1F2}\u{1F1E6}', awayTeam: 'Canad\u00e1', awayFlag: '\u{1F1E8}\u{1F1E6}', matchDate: '2026-06-15T16:00:00', status: 'FINISHED', homeScore: 3, awayScore: 0 },
    // Grupo A - Fecha 3
    { id: 'a5', round: 'Grupo A - Fecha 3', homeTeam: 'USA', homeFlag: '\u{1F1FA}\u{1F1F8}', awayTeam: 'Canad\u00e1', awayFlag: '\u{1F1E8}\u{1F1E6}', matchDate: '2026-06-19T13:00:00', status: 'FINISHED', homeScore: 2, awayScore: 0 },
    { id: 'a6', round: 'Grupo A - Fecha 3', homeTeam: 'Ecuador', homeFlag: '\u{1F1EA}\u{1F1E8}', awayTeam: 'Marruecos', awayFlag: '\u{1F1F2}\u{1F1E6}', matchDate: '2026-06-19T16:00:00', status: 'FINISHED', homeScore: 0, awayScore: 2 },
    // Grupo B - Fecha 1
    { id: 'b1', round: 'Grupo B - Fecha 1', homeTeam: 'Brasil', homeFlag: '\u{1F1E7}\u{1F1F7}', awayTeam: 'Nigeria', awayFlag: '\u{1F1F3}\u{1F1EC}', matchDate: '2026-06-12T18:00:00', status: 'FINISHED', homeScore: 3, awayScore: 0 },
    { id: 'b2', round: 'Grupo B - Fecha 1', homeTeam: 'Jap\u00f3n', homeFlag: '\u{1F1EF}\u{1F1F5}', awayTeam: 'Serbia', awayFlag: '\u{1F1F7}\u{1F1F8}', matchDate: '2026-06-12T21:00:00', status: 'FINISHED', homeScore: 2, awayScore: 2 },
    // Grupo B - Fecha 2
    { id: 'b3', round: 'Grupo B - Fecha 2', homeTeam: 'Brasil', homeFlag: '\u{1F1E7}\u{1F1F7}', awayTeam: 'Jap\u00f3n', awayFlag: '\u{1F1EF}\u{1F1F5}', matchDate: '2026-06-16T18:00:00', status: 'FINISHED', homeScore: 1, awayScore: 0 },
    { id: 'b4', round: 'Grupo B - Fecha 2', homeTeam: 'Nigeria', homeFlag: '\u{1F1F3}\u{1F1EC}', awayTeam: 'Serbia', awayFlag: '\u{1F1F7}\u{1F1F8}', matchDate: '2026-06-16T21:00:00', status: 'FINISHED', homeScore: 1, awayScore: 1 },
    // Grupo C - Fecha 1
    { id: 'm1', round: 'Grupo C - Fecha 1', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'Australia', awayFlag: '\u{1F1E6}\u{1F1FA}', matchDate: '2026-06-11T21:00:00', status: 'FINISHED', homeScore: 2, awayScore: 0 },
    { id: 'm2', round: 'Grupo C - Fecha 1', homeTeam: 'Francia', homeFlag: '\u{1F1EB}\u{1F1F7}', awayTeam: 'M\u00e9xico', awayFlag: '\u{1F1F2}\u{1F1FD}', matchDate: '2026-06-11T18:00:00', status: 'FINISHED', homeScore: 3, awayScore: 1 },
    // Grupo C - Fecha 2
    { id: 'm3', round: 'Grupo C - Fecha 2', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'Francia', awayFlag: '\u{1F1EB}\u{1F1F7}', matchDate: '2026-06-15T21:00:00', status: 'FINISHED', homeScore: 1, awayScore: 1 },
    { id: 'm4', round: 'Grupo C - Fecha 2', homeTeam: 'M\u00e9xico', homeFlag: '\u{1F1F2}\u{1F1FD}', awayTeam: 'Australia', awayFlag: '\u{1F1E6}\u{1F1FA}', matchDate: '2026-06-15T18:00:00', status: 'FINISHED', homeScore: 2, awayScore: 0 },
    // Grupo C - Fecha 3
    { id: 'm5', round: 'Grupo C - Fecha 3', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'M\u00e9xico', awayFlag: '\u{1F1F2}\u{1F1FD}', matchDate: '2026-06-19T21:00:00', status: 'FINISHED', homeScore: 2, awayScore: 1 },
    { id: 'm6', round: 'Grupo C - Fecha 3', homeTeam: 'Francia', homeFlag: '\u{1F1EB}\u{1F1F7}', awayTeam: 'Australia', awayFlag: '\u{1F1E6}\u{1F1FA}', matchDate: '2026-06-19T18:00:00', status: 'FINISHED', homeScore: 1, awayScore: 0 },
    // Grupo D - Fecha 1
    { id: 'd1', round: 'Grupo D - Fecha 1', homeTeam: 'Espa\u00f1a', homeFlag: '\u{1F1EA}\u{1F1F8}', awayTeam: 'Dinamarca', awayFlag: '\u{1F1E9}\u{1F1F0}', matchDate: '2026-06-13T18:00:00', status: 'FINISHED', homeScore: 2, awayScore: 0 },
    { id: 'd2', round: 'Grupo D - Fecha 1', homeTeam: 'Alemania', homeFlag: '\u{1F1E9}\u{1F1EA}', awayTeam: 'Colombia', awayFlag: '\u{1F1E8}\u{1F1F4}', matchDate: '2026-06-13T21:00:00', status: 'FINISHED', homeScore: 1, awayScore: 2 },
    // Octavos de Final
    { id: 'm7', round: 'Octavos de Final', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'Pa\u00edses Bajos', awayFlag: '\u{1F1F3}\u{1F1F1}', matchDate: '2026-06-28T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm8', round: 'Octavos de Final', homeTeam: 'Francia', homeFlag: '\u{1F1EB}\u{1F1F7}', awayTeam: 'Jap\u00f3n', awayFlag: '\u{1F1EF}\u{1F1F5}', matchDate: '2026-06-28T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm9', round: 'Octavos de Final', homeTeam: 'Brasil', homeFlag: '\u{1F1E7}\u{1F1F7}', awayTeam: 'Alemania', awayFlag: '\u{1F1E9}\u{1F1EA}', matchDate: '2026-06-29T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm10', round: 'Octavos de Final', homeTeam: 'Espa\u00f1a', homeFlag: '\u{1F1EA}\u{1F1F8}', awayTeam: 'Colombia', awayFlag: '\u{1F1E8}\u{1F1F4}', matchDate: '2026-06-29T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm11', round: 'Octavos de Final', homeTeam: 'USA', homeFlag: '\u{1F1FA}\u{1F1F8}', awayTeam: 'Senegal', awayFlag: '\u{1F1F8}\u{1F1F3}', matchDate: '2026-06-30T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm12', round: 'Octavos de Final', homeTeam: 'Inglaterra', homeFlag: '\u{1F1EC}\u{1F1E7}', awayTeam: 'Corea del Sur', awayFlag: '\u{1F1F0}\u{1F1F7}', matchDate: '2026-06-30T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm13', round: 'Octavos de Final', homeTeam: 'Marruecos', homeFlag: '\u{1F1F2}\u{1F1E6}', awayTeam: 'Portugal', awayFlag: '\u{1F1F5}\u{1F1F9}', matchDate: '2026-07-01T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'm14', round: 'Octavos de Final', homeTeam: 'M\u00e9xico', homeFlag: '\u{1F1F2}\u{1F1FD}', awayTeam: 'Italia', awayFlag: '\u{1F1EE}\u{1F1F9}', matchDate: '2026-07-01T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    // Cuartos de Final
    { id: 'qf1', round: 'Cuartos de Final', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-04T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'qf2', round: 'Cuartos de Final', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-04T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'qf3', round: 'Cuartos de Final', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-05T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'qf4', round: 'Cuartos de Final', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-05T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    // Semifinales
    { id: 'sf1', round: 'Semifinales', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-08T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'sf2', round: 'Semifinales', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-09T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    // Tercer Puesto
    { id: 'tp1', round: 'Tercer Puesto', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-18T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    // Final
    { id: 'fin1', round: 'Final', homeTeam: 'Por definir', homeFlag: '\u{1F3F3}\u{FE0F}', awayTeam: 'Por definir', awayFlag: '\u{1F3F3}\u{FE0F}', matchDate: '2026-07-19T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
  ],
  p2: [
    { id: 'p2m1', round: 'Grupo C - Fecha 1', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'Australia', awayFlag: '\u{1F1E6}\u{1F1FA}', matchDate: '2026-06-11T21:00:00', status: 'FINISHED', homeScore: 2, awayScore: 0 },
    { id: 'p2m2', round: 'Grupo C - Fecha 1', homeTeam: 'Francia', homeFlag: '\u{1F1EB}\u{1F1F7}', awayTeam: 'M\u00e9xico', awayFlag: '\u{1F1F2}\u{1F1FD}', matchDate: '2026-06-11T18:00:00', status: 'FINISHED', homeScore: 3, awayScore: 1 },
    { id: 'p2m3', round: 'Grupo C - Fecha 2', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'Francia', awayFlag: '\u{1F1EB}\u{1F1F7}', matchDate: '2026-06-15T21:00:00', status: 'FINISHED', homeScore: 1, awayScore: 1 },
    { id: 'p2m4', round: 'Grupo C - Fecha 2', homeTeam: 'M\u00e9xico', homeFlag: '\u{1F1F2}\u{1F1FD}', awayTeam: 'Australia', awayFlag: '\u{1F1E6}\u{1F1FA}', matchDate: '2026-06-15T18:00:00', status: 'FINISHED', homeScore: 2, awayScore: 0 },
    { id: 'p2m7', round: 'Octavos de Final', homeTeam: 'Argentina', homeFlag: '\u{1F1E6}\u{1F1F7}', awayTeam: 'Pa\u00edses Bajos', awayFlag: '\u{1F1F3}\u{1F1F1}', matchDate: '2026-06-28T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'p2m8', round: 'Octavos de Final', homeTeam: 'Francia', homeFlag: '\u{1F1EB}\u{1F1F7}', awayTeam: 'Jap\u00f3n', awayFlag: '\u{1F1EF}\u{1F1F5}', matchDate: '2026-06-28T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'p2m9', round: 'Octavos de Final', homeTeam: 'Brasil', homeFlag: '\u{1F1E7}\u{1F1F7}', awayTeam: 'Alemania', awayFlag: '\u{1F1E9}\u{1F1EA}', matchDate: '2026-06-29T21:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
    { id: 'p2m10', round: 'Octavos de Final', homeTeam: 'Espa\u00f1a', homeFlag: '\u{1F1EA}\u{1F1F8}', awayTeam: 'Colombia', awayFlag: '\u{1F1E8}\u{1F1F4}', matchDate: '2026-06-29T18:00:00', status: 'UPCOMING', homeScore: null, awayScore: null },
  ],
  p3: [],
};

const MOCK_PREDICTIONS = {
  a1: { home: 2, away: 1 }, a2: { home: 1, away: 1 }, a3: { home: 2, away: 0 }, a4: { home: 2, away: 1 },
  a5: { home: 3, away: 0 }, a6: { home: 1, away: 1 },
  b1: { home: 2, away: 0 }, b2: { home: 1, away: 2 }, b3: { home: 2, away: 1 }, b4: { home: 0, away: 0 },
  m1: { home: 2, away: 0 }, m2: { home: 2, away: 1 }, m3: { home: 2, away: 1 }, m4: { home: 1, away: 0 },
  m5: { home: 2, away: 1 }, m6: { home: 2, away: 0 },
  d1: { home: 1, away: 0 }, d2: { home: 2, away: 2 },
  p2m1: { home: 2, away: 0 }, p2m2: { home: 2, away: 1 }, p2m3: { home: 2, away: 1 }, p2m4: { home: 1, away: 0 },
};

const MOCK_LEADERBOARDS = {
  p1: [
    { rank: 1, name: 'Mart\u00edn L\u00f3pez', points: 32, exact: 8, winner: 5, wrong: 5 },
    { rank: 2, name: 'Ana Garc\u00eda', points: 28, exact: 7, winner: 4, wrong: 7 },
    { rank: 3, name: 'Lucas Fern\u00e1ndez', points: 26, exact: 6, winner: 5, wrong: 7 },
    { rank: 4, name: '__ME__', points: 24, exact: 5, winner: 6, wrong: 7, isCurrentUser: true },
    { rank: 5, name: 'Diego Mart\u00ednez', points: 22, exact: 5, winner: 4, wrong: 9 },
    { rank: 6, name: 'Sof\u00eda Romero', points: 20, exact: 4, winner: 5, wrong: 9 },
    { rank: 7, name: 'Pablo Navarro', points: 18, exact: 4, winner: 3, wrong: 11 },
    { rank: 8, name: 'Julia Torres', points: 15, exact: 3, winner: 3, wrong: 12 },
    { rank: 9, name: 'Mat\u00edas Sosa', points: 13, exact: 2, winner: 4, wrong: 12 },
    { rank: 10, name: 'Camila D\u00edaz', points: 11, exact: 2, winner: 2, wrong: 14 },
    { rank: 11, name: 'Franco Guti\u00e9rrez', points: 8, exact: 1, winner: 2, wrong: 15 },
    { rank: 12, name: 'Valentina Paz', points: 5, exact: 0, winner: 2, wrong: 16 },
  ],
  p2: [
    { rank: 1, name: 'Carlos Ruiz', points: 8, exact: 2, winner: 2, wrong: 0 },
    { rank: 2, name: '__ME__', points: 5, exact: 1, winner: 2, wrong: 1, isCurrentUser: true },
    { rank: 3, name: 'Mar\u00eda Santos', points: 4, exact: 1, winner: 1, wrong: 2 },
    { rank: 4, name: 'Pedro \u00c1lvarez', points: 3, exact: 1, winner: 0, wrong: 3 },
    { rank: 5, name: 'Laura Vega', points: 2, exact: 0, winner: 2, wrong: 2 },
  ],
};

// ══════════════════════════════════════════════════════════════
//  P R O D E   C O M P O N E N T
// ══════════════════════════════════════════════════════════════
const Prode = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const userName = user?.name || 'Vos';

  // ── State ──────────────────────────────────────────────────
  const [view, setView] = useState('hub');
  const [selectedProdeId, setSelectedProdeId] = useState(null);
  const [activeTab, setActiveTab] = useState('matches');
  const [communityFilter, setCommunityFilter] = useState('all');
  const [prodes, setProdes] = useState(MOCK_PRODES);
  const [allMatches, setAllMatches] = useState(MOCK_MATCHES);
  const [predictions, setPredictions] = useState(MOCK_PREDICTIONS);
  const [leaderboards] = useState(MOCK_LEADERBOARDS);
  const [unsaved, setUnsaved] = useState(new Set());
  const [mobileTab, setMobileTab] = useState('matches');
  const [shareToast, setShareToast] = useState(false);

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ communityId: '', exactPts: 3, winnerPts: 1 });

  // ── Computed ───────────────────────────────────────────────
  const communities = useMemo(() => {
    const seen = new Map();
    prodes.forEach(p => { if (!seen.has(p.communityId)) seen.set(p.communityId, p.communityName); });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [prodes]);

  const filteredProdes = useMemo(() => {
    if (communityFilter === 'all') return prodes;
    return prodes.filter(p => p.communityId === communityFilter);
  }, [prodes, communityFilter]);

  const selectedProde = prodes.find(p => p.id === selectedProdeId) || null;
  const prodeMatches = useMemo(() => allMatches[selectedProdeId] || [], [allMatches, selectedProdeId]);
  const myStats = selectedProdeId ? (leaderboards[selectedProdeId]?.find(e => e.isCurrentUser) || null) : null;
  const unsavedCount = unsaved.size;

  const scrollRef = useRef(null);
  const scrollByColumn = (dir) => {
    if (!scrollRef.current) return;
    const col = scrollRef.current.querySelector('[data-round-col]');
    if (!col) return;
    scrollRef.current.scrollBy({ left: dir * (col.offsetWidth + 1), behavior: 'smooth' });
  };

  // All matches grouped by round in chronological order (finished first, then upcoming)
  const allByRound = useMemo(() => groupByRound(prodeMatches), [prodeMatches]);

  // Auto-scroll to first upcoming round when entering detail view
  useEffect(() => {
    if (view !== 'detail' || !scrollRef.current) return;
    const cols = scrollRef.current.querySelectorAll('[data-round-col]');
    if (!cols.length) return;
    const idx = allByRound.findIndex(([, matches]) => matches.some(m => m.status === 'UPCOMING'));
    // Show one finished round to the left for context, or the last column if all finished
    const targetIdx = idx > 0 ? idx - 1 : idx >= 0 ? idx : cols.length - 1;
    if (cols[targetIdx]) cols[targetIdx].scrollIntoView({ behavior: 'auto', inline: 'start', block: 'nearest' });
  }, [view, selectedProdeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ───────────────────────────────────────────────
  const selectProde = (p) => {
    setSelectedProdeId(p.id);
    setView('detail');
    setActiveTab('matches');
    setMobileTab('matches');
    setUnsaved(new Set());
  };

  const goBack = () => { setView('hub'); setSelectedProdeId(null); };

  const updatePrediction = (matchId, side, value) => {
    const num = value.replace(/[^0-9]/g, '');
    if (num.length > 2) return;
    setPredictions(prev => ({ ...prev, [matchId]: { ...prev[matchId], [side]: num === '' ? '' : parseInt(num) } }));
    setUnsaved(prev => new Set([...prev, matchId]));
  };

  const savePredictions = () => {
    const incomplete = [];
    unsaved.forEach(matchId => {
      const pred = predictions[matchId];
      if (!pred || pred.home === '' || pred.away === '') incomplete.push(matchId);
    });
    setUnsaved(incomplete.length > 0 ? new Set(incomplete) : new Set());
  };

  const joinProde = (prodeId) => {
    setProdes(prev => prev.map(p => p.id === prodeId ? { ...p, joined: true, participantCount: p.participantCount + 1 } : p));
  };

  const shareProde = () => {
    const url = `${window.location.origin}/prode?id=${selectedProdeId}`;
    navigator.clipboard?.writeText(url).then(() => {
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2500);
    }).catch(() => {});
  };

  const handleCreateProde = () => {
    if (!createForm.communityId) return;
    const id = `p${Date.now()}`;
    const comm = communities.find(c => c.id === createForm.communityId);
    setProdes(prev => [{
      id, communityId: createForm.communityId,
      communityName: comm?.name || 'Mi Comunidad',
      status: 'UPCOMING', createdBy: 'SELF',
      scoring: { exact: createForm.exactPts, winner: createForm.winnerPts },
      participantCount: 1, totalMatches: 0, playedMatches: 0, joined: true,
    }, ...prev]);
    setAllMatches(prev => ({ ...prev, [id]: [] }));
    setShowCreate(false);
    setCreateForm({ communityId: '', exactPts: 3, winnerPts: 1 });
    selectProde({ id, status: 'UPCOMING' });
  };

  // ── Score display helper ───────────────────────────────────
  const getScoreDisplay = (matchId, match) => {
    const pred = predictions[matchId];
    if (!pred || pred.home === '' || pred.away === '') return { label: t('Sin pron\u00f3stico'), cls: 'text-on-surface-variant/40', icon: 'remove', badge: null };
    const result = scorePrediction(pred, match, selectedProde?.scoring);
    if (!result) return { label: `${pred.home} - ${pred.away}`, cls: 'text-on-surface-variant', icon: 'schedule', badge: null };
    if (result.type === 'exact') return { label: `${pred.home} - ${pred.away}`, cls: 'text-emerald-700', icon: 'star', badge: `+${result.points}`, badgeCls: 'bg-emerald-500/15 text-emerald-700' };
    if (result.type === 'winner') return { label: `${pred.home} - ${pred.away}`, cls: 'text-amber-700', icon: 'check_circle', badge: `+${result.points}`, badgeCls: 'bg-amber-500/15 text-amber-700' };
    return { label: `${pred.home} - ${pred.away}`, cls: 'text-red-600', icon: 'cancel', badge: '+0', badgeCls: 'bg-red-500/12 text-red-600' };
  };

  // ══════════════════════════════════════════════════════════════
  //  R E N D E R
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-container-lowest">

      {/* ═══════════════════ HUB VIEW ═══════════════════ */}
      {view === 'hub' && (
        <>
          {/* Sticky header */}
          <div className="px-6 tablet-sm:px-10 py-5 sticky top-0 bg-surface-container-lowest/80 backdrop-blur-xl z-20 border-b border-outline-variant/10 shrink-0">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-xl text-amber-600">emoji_events</span>
                </div>
                <div>
                  <h1 className="text-xl font-black text-on-surface tracking-tight">{t('Mundial 2026')}</h1>
                  <p className="text-[11px] text-on-surface-variant/60">{t('Pron\u00f3sticos por comunidad')}</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary text-on-primary font-semibold text-sm hover:opacity-90 transition-all active:scale-95 shadow-sm"
              >
                <span className="material-symbols-outlined text-lg">add</span>
                <span className="hidden mobile-md:inline">{t('Crear Prode')}</span>
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto hide-scrollbar">
            <div className="max-w-5xl mx-auto px-6 tablet-sm:px-10 py-8">

              {/* World Cup hero */}
              <div className="mb-8">
                <div className="flex items-baseline gap-3 mb-1.5">
                  <h2 className="text-3xl font-black text-on-surface tracking-tight">{t('Mundial 2026')}</h2>
                  <span className="text-base">&#x1F1FA;&#x1F1F8; &#x1F1F2;&#x1F1FD; &#x1F1E8;&#x1F1E6;</span>
                </div>
                <p className="text-sm text-on-surface-variant mb-6">11 {t('jun')} – 19 {t('jul')} 2026 · 48 {t('selecciones')}</p>

                {/* Phase progress */}
                <div className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar pb-1">
                  {WC_PHASES.map((phase, i) => (
                    <React.Fragment key={phase.key}>
                      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                        i < CURRENT_PHASE_INDEX
                          ? 'bg-primary/10 text-primary'
                          : i === CURRENT_PHASE_INDEX
                          ? 'bg-primary text-on-primary shadow-sm'
                          : 'bg-surface-container text-on-surface-variant/50'
                      }`}>
                        <span className="material-symbols-outlined text-sm">{phase.icon}</span>
                        {phase.label}
                      </div>
                      {i < WC_PHASES.length - 1 && (
                        <div className={`w-6 h-0.5 rounded shrink-0 ${i < CURRENT_PHASE_INDEX ? 'bg-primary/40' : 'bg-surface-container-high'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Community filter */}
              {communities.length > 1 && (
                <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar pb-1">
                  <button
                    onClick={() => setCommunityFilter('all')}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                      communityFilter === 'all'
                        ? 'bg-primary text-on-primary shadow-sm'
                        : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                    }`}
                  >
                    {t('Todos')}
                  </button>
                  {communities.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setCommunityFilter(c.id)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                        communityFilter === c.id
                          ? 'bg-primary text-on-primary shadow-sm'
                          : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Prode cards */}
              {filteredProdes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-20 h-20 rounded-3xl bg-surface-container flex items-center justify-center mb-5">
                    <span className="material-symbols-outlined text-4xl text-on-surface-variant/30">emoji_events</span>
                  </div>
                  <p className="text-base font-bold text-on-surface mb-1">{t('Sin prodes')}</p>
                  <p className="text-sm text-on-surface-variant leading-relaxed max-w-sm">
                    {t('Cre\u00e1 un prode para tu comunidad y empez\u00e1 a competir con pron\u00f3sticos del Mundial.')}
                  </p>
                  <button
                    onClick={() => setShowCreate(true)}
                    className="mt-5 flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary text-on-primary font-semibold text-sm"
                  >
                    <span className="material-symbols-outlined text-lg">add</span> {t('Crear Prode')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 mobile-md:grid-cols-2 tablet-lg:grid-cols-3 gap-5">
                  {filteredProdes.map(p => {
                    const status = STATUS_BADGE[p.status] || STATUS_BADGE.ACTIVE;
                    const stats = leaderboards[p.id]?.find(e => e.isCurrentUser);
                    const progress = p.totalMatches > 0 ? (p.playedMatches / p.totalMatches) * 100 : 0;

                    return (
                      <div key={p.id} className="bg-surface-container-lowest border border-outline-variant/10 rounded-2xl p-6 hover:shadow-lg hover:border-primary/20 transition-all group">
                        {/* Header: community + status */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                              {p.communityName.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-base font-bold text-on-surface truncate group-hover:text-primary transition-colors">{p.communityName}</h3>
                              <p className="text-xs text-on-surface-variant">{p.participantCount} {t('participantes')}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 ml-2">
                            <div className={`w-1.5 h-1.5 rounded-full ${status.dot} ${p.status === 'ACTIVE' ? 'animate-pulse' : ''}`} />
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status.cls}`}>{t(status.label)}</span>
                          </div>
                        </div>

                        {/* Progress */}
                        {p.totalMatches > 0 && (
                          <div className="mb-4">
                            <div className="flex justify-between text-xs text-on-surface-variant mb-1.5">
                              <span>{p.playedMatches} {t('de')} {p.totalMatches} {t('partidos')}</span>
                              <span className="font-semibold">{Math.round(progress)}%</span>
                            </div>
                            <div className="h-1.5 bg-surface-container-high rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        )}

                        {/* User stats */}
                        {p.joined && stats && (
                          <div className="flex items-center gap-3 bg-primary/[0.04] rounded-xl px-4 py-3 mb-4 border border-primary/10">
                            <div className="flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-sm text-primary">emoji_events</span>
                              <span className="text-sm font-black text-primary">#{stats.rank}</span>
                            </div>
                            <div className="h-4 w-px bg-outline-variant/20" />
                            <span className="text-sm font-bold text-on-surface">{stats.points} pts</span>
                            <div className="flex items-center gap-2 text-[11px] text-on-surface-variant ml-auto">
                              <span title={t('Exactos')} className="text-emerald-600">&#x2B50;{stats.exact}</span>
                              <span title={t('Ganador')} className="text-amber-600">&#x2713;{stats.winner}</span>
                            </div>
                          </div>
                        )}

                        {/* CTA */}
                        {p.joined ? (
                          <button onClick={() => selectProde(p)}
                            className="w-full py-3 rounded-xl bg-primary/8 text-primary font-semibold text-sm hover:bg-primary/15 transition-colors flex items-center justify-center gap-2"
                          >
                            {t('Ver pron\u00f3sticos')} <span className="material-symbols-outlined text-base">arrow_forward</span>
                          </button>
                        ) : (
                          <button onClick={() => joinProde(p.id)}
                            className="w-full py-3 rounded-xl bg-primary text-on-primary font-semibold text-sm hover:opacity-90 transition-colors flex items-center justify-center gap-2 shadow-sm"
                          >
                            <span className="material-symbols-outlined text-base">person_add</span> {t('Unirse al prode')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════ DETAIL VIEW ═══════════════════ */}
      {view === 'detail' && selectedProde && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* ── Detail Sticky Header ── */}
          <header className="shrink-0 sticky top-0 z-20 bg-surface-container-lowest/80 backdrop-blur-xl border-b border-outline-variant/10">
            <div className="px-6 tablet-sm:px-10 py-4">
              {/* Top row */}
              <div className="flex items-center gap-3 mb-3">
                <button onClick={goBack} className="p-2 -ml-2 rounded-xl hover:bg-surface-container transition-colors">
                  <span className="material-symbols-outlined text-on-surface-variant text-xl">arrow_back</span>
                </button>
                <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-xl text-amber-600">emoji_events</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-on-surface truncate">{selectedProde.communityName}</h2>
                  <p className="text-sm text-on-surface-variant">
                    {t('Mundial 2026')} · {selectedProde.participantCount} {t('participantes')}
                  </p>
                </div>
                <button onClick={shareProde} className="p-2.5 rounded-xl hover:bg-surface-container transition-colors shrink-0" title={t('Compartir')}>
                  <span className="material-symbols-outlined text-on-surface-variant text-xl">share</span>
                </button>
              </div>

              {/* Compact stats */}
              {selectedProde.joined && myStats && (
                <div className="flex items-center gap-3 text-sm mb-3 px-1">
                  <span className="font-black text-primary text-base">#{myStats.rank}</span>
                  <span className="text-on-surface-variant/30">·</span>
                  <span className="font-bold text-on-surface">{myStats.points} pts</span>
                  <span className="text-on-surface-variant/30">·</span>
                  <span className="text-emerald-600 font-semibold">&#x2B50;{myStats.exact}</span>
                  <span className="text-amber-600 font-semibold">&#x2713;{myStats.winner}</span>
                  <span className="text-red-500 font-semibold">&#x2717;{myStats.wrong}</span>
                </div>
              )}

              {/* Join banner */}
              {!selectedProde.joined && (
                <div className="flex items-center gap-3 bg-primary/[0.06] border border-primary/15 rounded-xl px-5 py-4 mb-3">
                  <span className="material-symbols-outlined text-primary text-xl">person_add</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold text-on-surface">{t('Unite a este prode')}</p>
                    <p className="text-sm text-on-surface-variant">{t('Particip\u00e1 con pron\u00f3sticos y compet\u00ed en el ranking.')}</p>
                  </div>
                  <button onClick={() => joinProde(selectedProdeId)}
                    className="px-5 py-2.5 rounded-xl bg-primary text-on-primary text-sm font-bold hover:opacity-90 transition-colors shrink-0"
                  >
                    {t('Unirme')}
                  </button>
                </div>
              )}

              {/* Tabs */}
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5 flex-1">
                  {[
                    { key: 'matches', label: t('Partidos'), count: prodeMatches.length },
                    { key: 'rules', label: t('Reglas'), count: null },
                  ].map(tab => (
                    <button key={tab.key}
                      onClick={() => { setActiveTab(tab.key); setMobileTab('matches'); }}
                      className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        activeTab === tab.key && mobileTab === 'matches'
                          ? 'bg-primary text-on-primary shadow-sm'
                          : 'text-on-surface-variant hover:bg-surface-container'
                      }`}
                    >
                      {tab.label} {tab.count != null && <span className="opacity-60 ml-1">{tab.count}</span>}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setMobileTab(mt => mt === 'ranking' ? 'matches' : 'ranking')}
                  className={`tablet-lg:hidden px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-1.5 ${
                    mobileTab === 'ranking'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'text-on-surface-variant hover:bg-surface-container'
                  }`}
                >
                  <span className="material-symbols-outlined text-base">leaderboard</span>
                  {t('Ranking')}
                </button>
              </div>
            </div>
          </header>

          {/* ── Content Area (carousel + sidebar) ── */}
          <div className="flex-1 flex min-h-0 overflow-hidden">

            {/* Main content — horizontal carousel */}
            <div className={`flex-1 flex flex-col overflow-hidden ${mobileTab === 'ranking' ? 'hidden tablet-lg:flex' : ''}`}>

              {/* ── MATCHES TAB — Unified timeline (scroll left = results, right = upcoming) ── */}
              {activeTab === 'matches' && mobileTab === 'matches' && (
                <>
                  {allByRound.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-16 text-center px-6">
                      <div className="w-20 h-20 rounded-2xl bg-surface-container flex items-center justify-center mb-5">
                        <span className="material-symbols-outlined text-4xl text-on-surface-variant/30">sports_soccer</span>
                      </div>
                      <p className="text-base font-bold text-on-surface mb-2">{t('No hay partidos a\u00fan')}</p>
                      <p className="text-sm text-on-surface-variant max-w-sm">
                        {t('Los partidos del Mundial aparecer\u00e1n ac\u00e1 cuando est\u00e9n programados.')}
                      </p>
                    </div>
                  ) : (
                    <div className="flex-1 relative min-h-0">
                      {/* Left/right nav arrows */}
                      <button onClick={() => scrollByColumn(-1)}
                        className="hidden tablet-sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-surface-container-lowest/90 border border-outline-variant/15 shadow-lg items-center justify-center hover:bg-surface-container transition-colors"
                      >
                        <span className="material-symbols-outlined text-on-surface-variant">chevron_left</span>
                      </button>
                      <button onClick={() => scrollByColumn(1)}
                        className="hidden tablet-sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-surface-container-lowest/90 border border-outline-variant/15 shadow-lg items-center justify-center hover:bg-surface-container transition-colors"
                      >
                        <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
                      </button>

                      {/* Horizontal scroll container */}
                      <div ref={scrollRef} className="flex h-full overflow-x-auto snap-x snap-mandatory hide-scrollbar">
                        {allByRound.map(([round, matches]) => {
                          const isFinishedRound = matches.every(m => m.status === 'FINISHED');
                          const roundPts = isFinishedRound ? matches.reduce((acc, m) => {
                            const r = scorePrediction(predictions[m.id], m, selectedProde.scoring);
                            return acc + (r?.points || 0);
                          }, 0) : null;

                          return (
                            <div key={round} data-round-col className="snap-start shrink-0 w-[85vw] mobile-md:w-[50vw] tablet-sm:w-[38vw] tablet-lg:w-[34vw] max-w-[420px] min-w-[300px] flex flex-col border-r border-outline-variant/10 last:border-r-0">
                              {/* Round header */}
                              <div className={`px-5 py-4 border-b border-outline-variant/10 shrink-0 ${isFinishedRound ? 'bg-surface-container/20' : 'bg-surface-container/30'}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={`material-symbols-outlined text-base ${isFinishedRound ? 'text-on-surface-variant/50' : 'text-primary'}`}>
                                      {isFinishedRound ? 'flag' : 'event'}
                                    </span>
                                    <h3 className="text-sm font-bold text-on-surface">{round}</h3>
                                    {isFinishedRound && (
                                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant/60">{t('Finalizado')}</span>
                                    )}
                                  </div>
                                  {roundPts != null && selectedProde.joined && (
                                    <span className="text-sm font-bold text-primary">{roundPts} pts</span>
                                  )}
                                </div>
                                <p className="text-xs text-on-surface-variant mt-0.5">
                                  {matches.length} {matches.length === 1 ? t('partido') : t('partidos')}
                                </p>
                              </div>

                              {/* Match cards */}
                              <div className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-3 pb-24">
                                {matches.map(match => {
                                  if (match.status === 'FINISHED') {
                                    // ── Finished match card ──
                                    const score = getScoreDisplay(match.id, match);
                                    const resultBg = score.badge
                                      ? score.badgeCls.includes('emerald') ? 'border-emerald-500/15' : score.badgeCls.includes('amber') ? 'border-amber-500/15' : 'border-red-500/10'
                                      : 'border-outline-variant/10';

                                    return (
                                      <div key={match.id} className={`bg-surface-container-lowest border rounded-2xl p-5 ${resultBg}`}>
                                        {/* Teams + actual score */}
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
                                            <span className="text-sm font-semibold text-on-surface truncate text-right">{match.homeTeam}</span>
                                            <span className="text-2xl leading-none shrink-0">{match.homeFlag}</span>
                                          </div>
                                          <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-2xl font-black text-on-surface w-7 text-right">{match.homeScore}</span>
                                            <span className="text-xs font-bold text-on-surface-variant/25">–</span>
                                            <span className="text-2xl font-black text-on-surface w-7">{match.awayScore}</span>
                                          </div>
                                          <div className="flex-1 flex items-center gap-2 min-w-0">
                                            <span className="text-2xl leading-none shrink-0">{match.awayFlag}</span>
                                            <span className="text-sm font-semibold text-on-surface truncate">{match.awayTeam}</span>
                                          </div>
                                        </div>
                                        {/* Prediction result */}
                                        <div className="flex items-center gap-2 pt-3 mt-3 border-t border-outline-variant/10">
                                          <span className={`material-symbols-outlined text-base ${score.cls}`}>{score.icon}</span>
                                          <span className={`text-sm font-medium ${score.cls}`}>
                                            {t('Tu pron\u00f3stico')}: {score.label}
                                          </span>
                                          {score.badge && (
                                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ml-auto ${score.badgeCls}`}>
                                              {score.badge}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }

                                  // ── Upcoming match card ──
                                  const pred = predictions[match.id];
                                  const dateInfo = fmtMatchDate(match.matchDate);
                                  const hasPrediction = pred && pred.home !== '' && pred.away !== '';
                                  const isUnsaved = unsaved.has(match.id);

                                  return (
                                    <div key={match.id} className={`bg-surface-container-lowest border rounded-2xl p-5 transition-all ${
                                      isUnsaved ? 'border-primary/30 ring-1 ring-primary/10' : 'border-outline-variant/10'
                                    }`}>
                                      {/* Date + tag */}
                                      <div className="flex items-center justify-between mb-4">
                                        <span className="text-xs text-on-surface-variant font-medium">{dateInfo.sub}</span>
                                        {dateInfo.tag === 'live' && (
                                          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-500 text-white animate-pulse">{dateInfo.main}</span>
                                        )}
                                        {dateInfo.tag === 'urgent' && (
                                          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-500/12 text-red-600 animate-pulse">{dateInfo.main}</span>
                                        )}
                                        {dateInfo.tag === 'soon' && (
                                          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/12 text-amber-700">{dateInfo.main}</span>
                                        )}
                                        {!dateInfo.tag && (
                                          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-surface-container-high text-on-surface-variant">{dateInfo.main}</span>
                                        )}
                                      </div>
                                      {/* Teams + score inputs */}
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
                                          <span className="text-sm font-semibold text-on-surface truncate text-right">{match.homeTeam}</span>
                                          <span className="text-2xl leading-none shrink-0">{match.homeFlag}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                          <input
                                            type="text" inputMode="numeric" maxLength={2}
                                            value={pred?.home ?? ''}
                                            onChange={e => updatePrediction(match.id, 'home', e.target.value)}
                                            disabled={!selectedProde.joined}
                                            className="w-11 h-11 rounded-xl bg-surface-container border-2 border-outline-variant/15 text-center font-black text-lg text-on-surface focus:border-primary/40 focus:ring-2 focus:ring-primary/10 focus:outline-none disabled:opacity-30 transition-all"
                                            placeholder="–"
                                          />
                                          <span className="text-xs font-bold text-on-surface-variant/25">–</span>
                                          <input
                                            type="text" inputMode="numeric" maxLength={2}
                                            value={pred?.away ?? ''}
                                            onChange={e => updatePrediction(match.id, 'away', e.target.value)}
                                            disabled={!selectedProde.joined}
                                            className="w-11 h-11 rounded-xl bg-surface-container border-2 border-outline-variant/15 text-center font-black text-lg text-on-surface focus:border-primary/40 focus:ring-2 focus:ring-primary/10 focus:outline-none disabled:opacity-30 transition-all"
                                            placeholder="–"
                                          />
                                        </div>
                                        <div className="flex-1 flex items-center gap-2 min-w-0">
                                          <span className="text-2xl leading-none shrink-0">{match.awayFlag}</span>
                                          <span className="text-sm font-semibold text-on-surface truncate">{match.awayTeam}</span>
                                        </div>
                                      </div>
                                      {/* Status */}
                                      {(hasPrediction || isUnsaved) && (
                                        <div className="mt-3 pt-3 border-t border-outline-variant/10">
                                          {hasPrediction && !isUnsaved && (
                                            <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                                              <span className="material-symbols-outlined text-sm">check_circle</span> {t('Guardado')}
                                            </span>
                                          )}
                                          {isUnsaved && (
                                            <span className="flex items-center gap-1.5 text-xs text-primary font-medium">
                                              <span className="material-symbols-outlined text-sm">edit</span> {t('Sin guardar')}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Floating save bar */}
                      {unsavedCount > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 px-6 pb-4 z-10">
                          <div className="flex items-center justify-between bg-primary/95 backdrop-blur-lg rounded-2xl px-6 py-4 shadow-xl border border-primary/20">
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-on-primary/80">edit_note</span>
                              <span className="text-sm font-medium text-on-primary/90">
                                {unsavedCount} {unsavedCount === 1 ? t('pron\u00f3stico sin guardar') : t('pron\u00f3sticos sin guardar')}
                              </span>
                            </div>
                            <button onClick={savePredictions}
                              className="px-6 py-2.5 rounded-xl bg-white/20 text-on-primary font-bold text-sm hover:bg-white/30 transition-colors active:scale-95"
                            >
                              {t('Guardar todo')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── RULES TAB ── */}
              {activeTab === 'rules' && mobileTab === 'matches' && (
                <div className="flex-1 overflow-y-auto hide-scrollbar">
                  <div className="max-w-lg px-6 tablet-sm:px-10 py-8">
                    <h3 className="text-xl font-bold text-on-surface mb-6 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary text-xl">menu_book</span>
                      {t('Reglas del Prode')}
                    </h3>

                    <div className="space-y-4">
                      <div className="flex gap-4 bg-emerald-500/[0.05] border border-emerald-500/15 rounded-2xl p-6">
                        <div className="w-14 h-14 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-emerald-600 text-xl">star</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1.5">
                            <p className="text-base font-bold text-on-surface">{t('Resultado exacto')}</p>
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-700">
                              +{selectedProde.scoring?.exact || 3} pts
                            </span>
                          </div>
                          <p className="text-sm text-on-surface-variant leading-relaxed">
                            {t('Acert\u00e1s el marcador exacto. Ej: predec\u00eds 2\u20131 y el resultado es 2\u20131.')}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 bg-amber-500/[0.05] border border-amber-500/15 rounded-2xl p-6">
                        <div className="w-14 h-14 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-amber-600 text-xl">check_circle</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1.5">
                            <p className="text-base font-bold text-on-surface">{t('Ganador correcto')}</p>
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700">
                              +{selectedProde.scoring?.winner || 1} pt
                            </span>
                          </div>
                          <p className="text-sm text-on-surface-variant leading-relaxed">
                            {t('Acert\u00e1s qui\u00e9n gana (o empate) pero no el marcador. Ej: predec\u00eds 3\u20131 y el resultado es 2\u20130.')}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 bg-red-500/[0.04] border border-red-500/10 rounded-2xl p-6">
                        <div className="w-14 h-14 rounded-xl bg-red-500/12 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-red-500 text-xl">cancel</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1.5">
                            <p className="text-base font-bold text-on-surface">{t('Error')}</p>
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-500/12 text-red-600">+0 pts</span>
                          </div>
                          <p className="text-sm text-on-surface-variant leading-relaxed">
                            {t('No acert\u00e1s ni el ganador ni el resultado. Ej: predec\u00eds 2\u20130 y el resultado es 1\u20131.')}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 bg-surface-container rounded-xl p-5 mt-2">
                        <span className="material-symbols-outlined text-on-surface-variant/50 mt-0.5 shrink-0">info</span>
                        <p className="text-sm text-on-surface-variant leading-relaxed">
                          {t('Las predicciones se bloquean cuando comienza el partido. Asegurate de completar tus pron\u00f3sticos antes del inicio.')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Leaderboard sidebar (desktop) / Mobile ranking ── */}
            <div className={`tablet-lg:w-80 tablet-lg:min-w-[320px] tablet-lg:border-l border-outline-variant/10 overflow-y-auto hide-scrollbar bg-surface-container-lowest
              ${mobileTab === 'ranking' ? 'flex-1' : 'hidden tablet-lg:block'}`}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-on-surface mb-6 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-xl">leaderboard</span>
                  {t('Ranking')}
                </h3>

                {(() => {
                  const lb = leaderboards[selectedProdeId] || [];
                  if (lb.length === 0) return (
                    <div className="text-center py-12">
                      <span className="material-symbols-outlined text-3xl text-on-surface-variant/30 mb-3 block">leaderboard</span>
                      <p className="text-sm text-on-surface-variant">{t('El ranking se completar\u00e1 cuando haya resultados.')}</p>
                    </div>
                  );

                  const top3 = lb.slice(0, 3);
                  const rest = lb.slice(3);
                  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
                  const medals = ['\u{1F948}', '\u{1F947}', '\u{1F949}'];
                  const heights = ['h-32', 'h-40', 'h-28'];

                  return (
                    <>
                      {/* Podium */}
                      {top3.length >= 3 && (
                        <div className="flex items-end justify-center gap-2.5 mb-8 px-2">
                          {podiumOrder.map((entry, i) => (
                            <div key={entry.rank} className={`flex-1 max-w-[110px] ${heights[i]} flex flex-col items-center justify-end ${
                              i === 1 ? 'bg-primary/[0.07] border-2 border-primary/15' : 'bg-surface-container border border-outline-variant/10'
                            } rounded-2xl p-3 relative`}>
                              <span className="text-2xl mb-1">{medals[i]}</span>
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm mb-1 ${
                                entry.isCurrentUser ? 'ring-2 ring-primary' : ''
                              }`} style={{ backgroundColor: getTeamColor(entry.name) }}>
                                {(entry.isCurrentUser ? userName : entry.name).charAt(0).toUpperCase()}
                              </div>
                              <p className={`text-xs font-bold text-center truncate w-full ${entry.isCurrentUser ? 'text-primary' : 'text-on-surface'}`}>
                                {entry.isCurrentUser ? userName : entry.name.split(' ')[0]}
                              </p>
                              <p className={`text-base font-black ${i === 1 ? 'text-primary' : 'text-on-surface'}`}>{entry.points}</p>
                              <p className="text-[10px] text-on-surface-variant">pts</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Table */}
                      <div className="space-y-1">
                        {(top3.length < 3 ? lb : rest).map(entry => (
                          <div key={entry.rank} className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                            entry.isCurrentUser ? 'bg-primary/[0.06] border border-primary/15' : 'hover:bg-surface-container/60'
                          }`}>
                            <span className={`w-7 text-center text-sm font-bold ${entry.isCurrentUser ? 'text-primary' : 'text-on-surface-variant'}`}>
                              {entry.rank}
                            </span>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                              entry.isCurrentUser ? 'ring-2 ring-primary' : ''
                            }`} style={{ backgroundColor: getTeamColor(entry.name) }}>
                              {(entry.isCurrentUser ? userName : entry.name).charAt(0).toUpperCase()}
                            </div>
                            <span className={`flex-1 text-sm font-semibold truncate ${entry.isCurrentUser ? 'text-primary' : 'text-on-surface'}`}>
                              {entry.isCurrentUser ? `${userName} (Vos)` : entry.name}
                            </span>
                            <span className={`text-sm font-bold ${entry.isCurrentUser ? 'text-primary' : 'text-on-surface'}`}>
                              {entry.points}
                            </span>
                            <div className="flex items-center gap-2 text-xs text-on-surface-variant/60">
                              <span title={t('Exactos')}>&#x2B50;{entry.exact}</span>
                              <span title={t('Ganador')}>&#x2713;{entry.winner}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ CREATE PRODE MODAL ═══════════════════ */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-outline-variant/15" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/15">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-amber-600">emoji_events</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-on-surface">{t('Crear Prode')}</h3>
                  <p className="text-xs text-on-surface-variant">{t('Mundial 2026')}</p>
                </div>
              </div>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-on-surface-variant">close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Community */}
              <div>
                <label className="text-sm font-semibold text-on-surface block mb-1.5">{t('Comunidad')}</label>
                <p className="text-xs text-on-surface-variant mb-2">{t('Eleg\u00ed para qu\u00e9 comunidad crear\u00e1s el prode del Mundial.')}</p>
                <select value={createForm.communityId} onChange={e => setCreateForm(f => ({ ...f, communityId: e.target.value }))}
                  className="w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">{t('Seleccionar comunidad')}</option>
                  {communities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Scoring */}
              <div>
                <label className="text-sm font-semibold text-on-surface block mb-1.5">{t('Puntuaci\u00f3n')}</label>
                <div className="flex gap-3">
                  <div className="flex-1 bg-surface-container rounded-xl p-3 text-center">
                    <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider mb-1">{t('Exacto')}</p>
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => setCreateForm(f => ({ ...f, exactPts: Math.max(1, f.exactPts - 1) }))}
                        className="w-7 h-7 rounded-lg bg-surface-container-high flex items-center justify-center text-on-surface-variant hover:bg-surface-container-highest transition-colors"
                      >–</button>
                      <span className="text-xl font-black text-on-surface w-6 text-center">{createForm.exactPts}</span>
                      <button onClick={() => setCreateForm(f => ({ ...f, exactPts: Math.min(10, f.exactPts + 1) }))}
                        className="w-7 h-7 rounded-lg bg-surface-container-high flex items-center justify-center text-on-surface-variant hover:bg-surface-container-highest transition-colors"
                      >+</button>
                    </div>
                  </div>
                  <div className="flex-1 bg-surface-container rounded-xl p-3 text-center">
                    <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider mb-1">{t('Ganador')}</p>
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => setCreateForm(f => ({ ...f, winnerPts: Math.max(1, f.winnerPts - 1) }))}
                        className="w-7 h-7 rounded-lg bg-surface-container-high flex items-center justify-center text-on-surface-variant hover:bg-surface-container-highest transition-colors"
                      >–</button>
                      <span className="text-xl font-black text-on-surface w-6 text-center">{createForm.winnerPts}</span>
                      <button onClick={() => setCreateForm(f => ({ ...f, winnerPts: Math.min(10, f.winnerPts + 1) }))}
                        className="w-7 h-7 rounded-lg bg-surface-container-high flex items-center justify-center text-on-surface-variant hover:bg-surface-container-highest transition-colors"
                      >+</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-outline-variant/15 bg-surface-container/30">
              <button onClick={() => setShowCreate(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-on-surface-variant hover:bg-surface-container transition-colors"
              >{t('Cancelar')}</button>
              <button onClick={handleCreateProde} disabled={!createForm.communityId}
                className="px-6 py-2.5 rounded-xl text-sm font-bold bg-primary text-on-primary hover:opacity-90 transition-all disabled:opacity-30 active:scale-95 shadow-sm"
              >{t('Crear Prode')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ SHARE TOAST ═══════════════════ */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-on-surface text-surface-container-lowest px-5 py-3 rounded-xl shadow-xl text-sm font-medium">
          <span className="material-symbols-outlined text-lg">check_circle</span>
          {t('Link copiado al portapapeles')}
        </div>
      )}
    </div>
  );
};

export default Prode;
